
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { Clipboard, ClipboardCheck, RefreshCw, Save, Key, Database } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import AdminStorageSettings from './AdminStorageSettings';

interface AdminSetting {
  id: string;
  setting_name: string;
  setting_value: string;
  description: string | null;
}

const SettingsManager = () => {
  const [settings, setSettings] = useState<AdminSetting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [copied, setCopied] = useState<Record<string, boolean>>({});
  const [initialSettingsCreated, setInitialSettingsCreated] = useState(false);
  
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('*')
        .order('setting_name', { ascending: true });
      
      if (error) throw error;
      setSettings(data || []);
      
      // Check if we need to create initial settings
      if (data && data.length > 0) {
        setInitialSettingsCreated(true);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load settings: ' + (error as Error).message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (id: string, value: string) => {
    setSettings(prevSettings => 
      prevSettings.map(setting => 
        setting.id === id ? { ...setting, setting_value: value } : setting
      )
    );
  };

  const handleSwitchChange = (id: string, checked: boolean) => {
    setSettings(prevSettings => 
      prevSettings.map(setting => 
        setting.id === id ? { ...setting, setting_value: checked ? 'true' : 'false' } : setting
      )
    );
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      for (const setting of settings) {
        const { error } = await supabase
          .from('admin_settings')
          .update({ 
            setting_value: setting.setting_value,
            updated_at: new Date().toISOString()
          })
          .eq('id', setting.id);
        
        if (error) throw error;
      }
      
      toast({
        title: 'Settings Saved',
        description: 'Your settings have been successfully updated',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Save Failed',
        description: 'Failed to save settings: ' + (error as Error).message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied({ ...copied, [id]: true });
      setTimeout(() => {
        setCopied({ ...copied, [id]: false });
      }, 2000);
    });
  };

  const getSettingsByCategory = (category: string) => {
    return settings.filter(setting => setting.setting_name.startsWith(category + '.'));
  };

  const isLoaded = !isLoading && settings.length > 0;

  const createInitialSettings = async () => {
    if (!initialSettingsCreated && !isLoading) {
      setIsSaving(true);
      try {
        // Check for existing settings to avoid duplicates
        const { data: existingSettings, error: fetchError } = await supabase
          .from('admin_settings')
          .select('setting_name')
          .in('setting_name', [
            'general.site_name', 
            'general.contact_email', 
            'general.currency',
            'general.enable_notifications',
            'security.login_attempts',
            'security.session_timeout',
            'security.enforce_2fa',
            'api.rate_limit',
            'api.enable_tracking'
          ]);
        
        if (fetchError) throw fetchError;
        
        // Create a map of existing settings
        const existingSettingNames = new Set((existingSettings || []).map(s => s.setting_name));
        
        const initialSettings = [
          { setting_name: 'general.site_name', setting_value: 'H44S Admin Portal', description: 'Name of the site displayed in the header' },
          { setting_name: 'general.contact_email', setting_value: 'contact@example.com', description: 'Main contact email address' },
          { setting_name: 'general.currency', setting_value: 'ZAR', description: 'Default currency for financial values' },
          { setting_name: 'general.enable_notifications', setting_value: 'true', description: 'Enable system notifications' },
          { setting_name: 'security.login_attempts', setting_value: '5', description: 'Maximum number of login attempts before lockout' },
          { setting_name: 'security.session_timeout', setting_value: '30', description: 'Session timeout in minutes' },
          { setting_name: 'security.enforce_2fa', setting_value: 'false', description: 'Enforce two-factor authentication for all users' },
          { setting_name: 'api.rate_limit', setting_value: '100', description: 'API rate limit per minute' },
          { setting_name: 'api.enable_tracking', setting_value: 'true', description: 'Enable API usage tracking' },
        ].filter(setting => !existingSettingNames.has(setting.setting_name));
        
        if (initialSettings.length > 0) {
          // Only insert settings that don't already exist
          const { error } = await supabase
            .from('admin_settings')
            .insert(initialSettings);
          
          if (error) throw error;
          
          toast({
            title: 'Settings Initialized',
            description: 'Default settings have been created',
          });
        } else {
          toast({
            title: 'Settings Initialized',
            description: 'Default settings already exist',
          });
        }
        
        setInitialSettingsCreated(true);
        fetchSettings();
      } catch (error) {
        console.error('Error creating initial settings:', error);
        toast({
          title: 'Initialization Failed',
          description: 'Failed to create default settings: ' + (error as Error).message,
          variant: 'destructive',
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  useEffect(() => {
    if (!isLoading && settings.length === 0 && !initialSettingsCreated) {
      createInitialSettings();
    }
  }, [isLoading, settings.length, initialSettingsCreated]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Platform Settings</CardTitle>
            <CardDescription>
              Configure global platform settings and preferences
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={fetchSettings} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={saveSettings} disabled={isLoading || isSaving || activeTab === "storage"}>
              {isSaving ? 'Saving...' : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && activeTab !== "storage" ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading settings...</p>
            </div>
          ) : (
            <Tabs defaultValue="general" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
                <TabsTrigger value="api">API</TabsTrigger>
                <TabsTrigger value="storage">
                  <Database className="mr-2 h-4 w-4" />
                  Storage
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="general">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">General Settings</h3>
                  <p className="text-sm text-gray-500">Configure basic platform settings</p>
                  <Separator />
                  
                  {getSettingsByCategory('general').map(setting => {
                    const settingKey = setting.setting_name.split('.')[1];
                    const isBool = setting.setting_value === 'true' || setting.setting_value === 'false';
                    
                    return (
                      <div className="grid gap-2 py-2" key={setting.id}>
                        <div className="flex justify-between items-center">
                          <Label htmlFor={setting.id} className="capitalize">{settingKey.replace(/_/g, ' ')}</Label>
                          {setting.description && (
                            <span className="text-xs text-gray-500">{setting.description}</span>
                          )}
                        </div>
                        
                        {isBool ? (
                          <Switch
                            id={setting.id}
                            checked={setting.setting_value === 'true'}
                            onCheckedChange={(checked) => handleSwitchChange(setting.id, checked)}
                          />
                        ) : (
                          <Input
                            id={setting.id}
                            value={setting.setting_value}
                            onChange={(e) => handleInputChange(setting.id, e.target.value)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
              
              <TabsContent value="security">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Security Settings</h3>
                  <p className="text-sm text-gray-500">Configure security and access settings</p>
                  <Separator />
                  
                  {getSettingsByCategory('security').map(setting => {
                    const settingKey = setting.setting_name.split('.')[1];
                    const isBool = setting.setting_value === 'true' || setting.setting_value === 'false';
                    
                    return (
                      <div className="grid gap-2 py-2" key={setting.id}>
                        <div className="flex justify-between items-center">
                          <Label htmlFor={setting.id} className="capitalize">{settingKey.replace(/_/g, ' ')}</Label>
                          {setting.description && (
                            <span className="text-xs text-gray-500">{setting.description}</span>
                          )}
                        </div>
                        
                        {isBool ? (
                          <Switch
                            id={setting.id}
                            checked={setting.setting_value === 'true'}
                            onCheckedChange={(checked) => handleSwitchChange(setting.id, checked)}
                          />
                        ) : (
                          <Input
                            id={setting.id}
                            value={setting.setting_value}
                            onChange={(e) => handleInputChange(setting.id, e.target.value)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
              
              <TabsContent value="api">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">API Settings</h3>
                  <p className="text-sm text-gray-500">Configure API access and integration settings</p>
                  <Separator />
                  
                  {getSettingsByCategory('api').map(setting => {
                    const settingKey = setting.setting_name.split('.')[1];
                    const isBool = setting.setting_value === 'true' || setting.setting_value === 'false';
                    
                    return (
                      <div className="grid gap-2 py-2" key={setting.id}>
                        <div className="flex justify-between items-center">
                          <Label htmlFor={setting.id} className="capitalize">{settingKey.replace(/_/g, ' ')}</Label>
                          {setting.description && (
                            <span className="text-xs text-gray-500">{setting.description}</span>
                          )}
                        </div>
                        
                        {isBool ? (
                          <Switch
                            id={setting.id}
                            checked={setting.setting_value === 'true'}
                            onCheckedChange={(checked) => handleSwitchChange(setting.id, checked)}
                          />
                        ) : (
                          <div className="flex gap-2">
                            <Input
                              id={setting.id}
                              value={setting.setting_value}
                              onChange={(e) => handleInputChange(setting.id, e.target.value)}
                              className="flex-1"
                            />
                            
                            {setting.setting_name.includes('key') && (
                              <Button
                                variant="outline"
                                size="icon" 
                                onClick={() => copyToClipboard(setting.setting_value, setting.id)}
                              >
                                {copied[setting.id] ? (
                                  <ClipboardCheck className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Clipboard className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  <div className="mt-6">
                    <Button variant="outline" className="w-full">
                      <Key className="mr-2 h-4 w-4" />
                      Generate New API Key
                    </Button>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="storage">
                <AdminStorageSettings />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsManager;
